import { useCallback } from 'react';
import { useViewNavigate } from '../../../hooks/useViewNavigate.js';
import { fetchScenarioCatalog, type PublicScenario } from '../../../lib/scenariosApi.js';
import { listenLevelOf } from '../../listen/bookLib.js';
import { getCatalog } from '../../listen/listenApi.js';
import { useListen } from '../../listen/ListenContext.js';
import type { BriefingContext } from '../../scenarios/scenarioTypes.js';
import { getProfile } from '../../../lib/profileApi.js';
import type { ShipPieceId } from './pieces.js';
import { pickBook, pickScene } from './pickers.js';

/** Los motivos del onboarding («¿Para qué querés el inglés?»): eligen capítulo y escena. */
async function goals(): Promise<string[]> {
  try {
    return (await getProfile()).learningGoals ?? [];
  } catch (err) {
    console.warn('[ship] sin los motivos del onboarding: decide el nivel', err);
    return [];
  }
}

/**
 * Lleva a la actividad de cada pieza, lo más cerca posible de lo que hay que hacer. Viaja
 * `state.shipPiece` para que la pantalla de destino muestre su cartel (`ShipHint`).
 */
export function useShipGo(opts: { cefr: string | null; programUnitId: string | null }) {
  const navigate = useViewNavigate();
  const listen = useListen();
  const { cefr, programUnitId } = opts;

  return useCallback(
    async (id: ShipPieceId, how: { choose?: boolean } = {}): Promise<void> => {
      const shipPiece = id;
      // «Elegir yo» (Antena): la Audioteca, para que elija el capítulo.
      if (id === 'antena' && how.choose) return navigate('/listen', { state: { shipPiece } });
      switch (id) {
        case 'cabina':
          return navigate('/conversation', { state: { shipPiece } });
        case 'motores':
          return navigate('/conversation', { state: { seed: 'translate-it', shipPiece } });
        case 'rumbo':
          return navigate('/program', {
            state: { shipPiece, ...(programUnitId ? { openUnitId: programUnitId } : {}) },
          });
        case 'bodega':
          return navigate('/library/explore', { state: { shipPiece } });
        case 'radar':
          return navigate('/progress', { state: { shipPiece } });
        case 'antena': {
          // «Que la elija la app»: un libro según sus motivos, el capítulo 1 en su nivel.
          if (!cefr) throw new Error('ship: la Antena necesita el nivel del alumno');
          // El de la cuenta, explícito: sin él, `openChapter` pedía el del selector de la Audioteca,
          // que a un alumno recién registrado le quedaba en B1 (#575, 20/10).
          const level = listenLevelOf(cefr);
          try {
            const [{ books }, gs] = await Promise.all([getCatalog(), goals()]);
            const choices = books.map((b) => {
              const ed = b.editions.long ?? b.editions.short;
              const first = ed?.keypoints.find((k) => k.order === 1);
              return { slug: b.slug, firstChapterLevels: first?.levels ?? [] };
            });
            const slug = pickBook(gs, level, choices);
            const book = books.find((b) => b.slug === slug);
            const edition = book?.editions.long ? 'long' : 'short';
            const first = book?.editions[edition]?.keypoints.find((k) => k.order === 1);
            if (!book || !first) throw new Error(`no está el capítulo 1 de ${slug}`);
            await listen.openChapter(book, first, edition, { play: true, cefr: level });
            return navigate('/listen/player', { state: { shipPiece } });
          } catch (err) {
            // Sin el capítulo (catálogo sin cargar en esta base): la Audioteca, con su cartel.
            console.warn('[ship] Antena: abro la Audioteca', err);
            return navigate('/listen', { state: { shipPiece } });
          }
        }
        case 'tren': {
          if (!cefr) throw new Error('ship: la escena del Tren necesita el nivel del alumno');
          let all: PublicScenario[];
          let gs: string[];
          try {
            const [{ groups }, g] = await Promise.all([fetchScenarioCatalog(), goals()]);
            all = groups.flatMap((x) => x.scenarios);
            gs = g;
          } catch (err) {
            // Sin catálogo (la red): la lista de escenas, con su cartel.
            console.warn('[ship] Tren: sin catálogo, abro las escenas', err);
            return navigate('/scenarios', { state: { shipPiece } });
          }
          // Fuera del catch a propósito (card 599): un nivel sin escena es un error del código, no
          // una red caída — antes caía callado a la lista (así pasó con A0).
          const wanted = pickScene(gs, cefr, all);
          const scenario = all.find((s) => s.id === wanted);
          if (!scenario) {
            console.warn(
              `[ship] Tren: la escena ${wanted} no está en el catálogo, abro las escenas`,
            );
            return navigate('/scenarios', { state: { shipPiece } });
          }
          const briefing: BriefingContext = { scenario, generated: false };
          return navigate('/scenarios/briefing', { state: { briefing, shipPiece } });
        }
      }
    },
    [navigate, listen, cefr, programUnitId],
  );
}
